import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarClock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';

const DEFAULT_DATE_RANGE = '3-month';

type DateRangeLabel = '1 Week' | '1 Month' | '3 Months' | '6 Months' | '12 Months';

const dateRangeValues = ['1-week', '1-month', '3-month', '6-month', '12-month'] as const;

export type DateRangeValue = (typeof dateRangeValues)[number];

const dateRangeOptions: { label: DateRangeLabel; value: DateRangeValue }[] = [
  { label: '1 Week', value: '1-week' },
  { label: '1 Month', value: '1-month' },
  { label: '3 Months', value: '3-month' },
  { label: '6 Months', value: '6-month' },
  { label: '12 Months', value: '12-month' },
];

const formSchema = z.object({
  dateRange: z.enum(dateRangeValues),
});

export type EmailDateRangeFormValues = z.infer<typeof formSchema>;

type EmailDateRangeSelectionFormProps = {
  onContinue: (values: EmailDateRangeFormValues) => void;
  onCancel: () => void;
};

export const EmailDateRangeSelectionForm = ({ onContinue, onCancel }: EmailDateRangeSelectionFormProps) => {
  const form = useForm<EmailDateRangeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dateRange: DEFAULT_DATE_RANGE,
    },
    mode: 'onChange',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => onContinue(values))}>
        <Stack space={10} align="stretch">
          <FormField
            control={form.control}
            name="dateRange"
            render={({ field }) => {
              const dateRangeOption = dateRangeOptions.find((option) => option.value === field.value);

              return (
                <FormItem>
                  <FormControl>
                    <Select
                      placeholder="Select time period"
                      defaultValue="3-month"
                      onValueChange={field.onChange}
                      triggerLabel={
                        <FormLabel className="text-xs font-medium text-muted-foreground">Search period</FormLabel>
                      }
                      triggerClassName="px-4 py-3 rounded-lg [&_.account-logo]:w-4 [&_.account-logo]:h-4 [&_.account-type]:hidden bg-white border-[0.5px] border-border text-black text-base font-medium"
                      selectedTriggerContent={
                        <Inline>
                          <CalendarClock strokeWidth={1} size={20} />

                          <p>{dateRangeOption?.label}</p>
                        </Inline>
                      }
                    >
                      {dateRangeOptions.map((option) => (
                        <Select.Item key={option.value} value={option.value}>
                          {option.label}
                        </Select.Item>
                      ))}
                    </Select>
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <Inline space={2}>
            <Button type="submit" variant="default" className="flex-1">
              Continue
            </Button>

            <Button variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Form>
  );
};
